/**
 * State construction: a new life, from a seed and a handful of setup choices.
 */

import { Rng, makeSeed } from './rng';
import type {
  AssetClassId,
  Habits,
  Holding,
  PlayerState,
  Skills,
  Traits,
} from './types';
import { HABIT_KEYS, SKILL_KEYS, TRAIT_KEYS } from './types';
import { clamp100, stageForAge } from './derived';
import { generateBoard } from './board';
import { CONTENT_VERSION, MOTIVATIONS, ORIGINS } from './content';
import { applyEffects, makePerson, pushLog } from './effects';

export const GAME_VERSION = '1.0.0';
export const SAVE_VERSION = 3;

export interface NewLifeOptions {
  name?: string;
  pronoun?: 'they' | 'she' | 'he';
  originId?: string;
  motivationId?: string;
  seed?: string;
  startAge?: number;
}

function emptySkills(): Skills {
  const out = {} as Skills;
  for (const k of SKILL_KEYS) out[k] = 0;
  return out;
}

function emptyTraits(): Traits {
  const out = {} as Traits;
  for (const k of TRAIT_KEYS) out[k] = 50;
  return out;
}

function emptyHabits(): Habits {
  const out = {} as Habits;
  for (const k of HABIT_KEYS) out[k] = 35;
  return out;
}

function emptyHoldings(): Record<AssetClassId, Holding> {
  const ids: AssetClassId[] = ['cash', 'conservative', 'balanced', 'growth', 'speculative', 'property'];
  const out = {} as Record<AssetClassId, Holding>;
  for (const id of ids) out[id] = { assetId: id, value: 0, contributed: 0 };
  return out;
}

export function createNewLife(options: NewLifeOptions = {}): PlayerState {
  const seed = options.seed ?? makeSeed();
  const rng = new Rng(seed);
  const startAge = options.startAge ?? 18;
  const originId = options.originId ?? 'origin.steady';
  const motivationId = options.motivationId ?? 'motive.security';
  const now = Date.now();

  const traits = emptyTraits();
  // Baseline personality: everyone is a little different before circumstance.
  for (const k of TRAIT_KEYS) traits[k] = clamp100(Math.round(rng.normal(50, 13)));

  const skills = emptySkills();
  for (const k of SKILL_KEYS) skills[k] = clamp100(Math.round(rng.normal(14, 6)));

  const state: PlayerState = {
    saveVersion: SAVE_VERSION,
    gameVersion: GAME_VERSION,
    contentVersion: CONTENT_VERSION,
    lifeId: `life_${rng.keyed('lifeId').int(100000, 999999)}_${seed.slice(0, 4)}`,
    runId: `run_${now.toString(36)}`,
    rng: rng.toState(),
    createdAt: now,
    updatedAt: now,

    identity: {
      name: options.name?.trim() || 'Avery Lane',
      pronoun: options.pronoun ?? 'they',
      originId,
      motivationId,
    },
    age: startAge,
    startAge,
    stage: stageForAge(startAge),
    alive: true,
    causeOfDeath: null,
    frailty: 0,

    stats: { health: 82, energy: 78, stress: 22, happiness: 62, purpose: 40 },
    skills,
    traits,
    habits: emptyHabits(),

    finance: {
      cash: 0,
      salary: 0,
      otherIncome: 0,
      fixedExpenses: 11000,
      lifestyleLevel: 2,
      debts: [],
      holdings: emptyHoldings(),
      retirementAssets: 0,
      property: { owned: false, value: 0, mortgageDebtId: null, purchaseAge: 0 },
      emergencyMonthsTarget: 6,
      lifetimeEarnings: 0,
      lifetimeSpending: 0,
      peakNetWorth: 0,
      bankruptcies: 0,
    },
    career: {
      status: 'none',
      fieldId: null,
      roleLevel: 0,
      tenure: 0,
      workload: 0,
      performance: 50,
      jobSecurity: 50,
      meaningAlignment: 50,
      reputation: 8,
      yearsExperience: 0,
      breaksTaken: 0,
      pivots: 0,
      layoffs: 0,
      promotions: 0,
      yearsSinceAdvance: 0,
    },
    education: {
      level: 'highschool',
      credentials: [],
      enrollment: null,
      yearsStudied: 0,
      totalCost: 0,
    },
    people: [],
    family: {
      maritalStatus: 'single',
      unionSinceAge: null,
      marriages: 0,
      divorces: 0,
      childrenBorn: 0,
      dependents: 0,
      householdHarmony: 60,
    },
    business: {
      active: false,
      archetypeId: null,
      name: '',
      capital: 0,
      revenue: 0,
      operatingCost: 0,
      growth: 0,
      risk: 0,
      health: 0,
      timeDemand: 0,
      valuation: 0,
      yearsRunning: 0,
      exitProceeds: null,
      failures: 0,
      exits: 0,
    },
    burnout: { stage: 'healthy', load: 8, yearsInStage: 0, episodes: 0, lastEpisodeAge: null },
    inertia: { pressure: 20, intentions: [], actedCount: 0, deferredCount: 0, decayedCount: 0 },
    conditions: [],
    aspirations: [],

    flags: { chosePath: false, everEmployed: false, ownsHome: false, hasChildren: false, founder: false },
    counters: {},

    log: [],
    decisions: [],
    regrets: [],
    achievements: [],
    scheduled: [],
    eventMemory: { lastSeenAge: {}, counts: {} },

    board: generateBoard(rng.keyed('board'), 'board.default', startAge),
    nodeId: '',
    visitedNodes: [],

    pendingEvents: ['milestone.eighteen'],
    market: { regime: 'normal', yearsInRegime: 0 },
    ending: null,
  };

  state.nodeId = state.board.startId;
  state.visitedNodes = [state.board.startId];

  const ctx = { state, rng, source: 'setup', sourceEventId: null };

  // Starting family. Parents always exist (alive or not); siblings sometimes.
  makePerson(ctx, { kind: 'parent', ageOffset: rng.int(24, 34), bond: { support: 55, trust: 60, closeness: 58 } });
  if (rng.chance(0.78)) {
    makePerson(ctx, { kind: 'parent', ageOffset: rng.int(24, 36), bond: { support: 52, trust: 58, closeness: 55 } });
  }
  const siblings = rng.weightedIndex([22, 40, 26, 12]);
  for (let i = 0; i < siblings; i++) {
    makePerson(ctx, { kind: 'sibling', ageOffset: rng.int(-6, 6), bond: { closeness: 52, trust: 55 } });
  }
  if (rng.chance(0.6)) {
    makePerson(ctx, { kind: 'friend', ageOffset: rng.int(-2, 2), tags: ['childhood'], bond: { closeness: 64, trust: 62 } });
  }

  // Origin effects, then trait/stat bias.
  const origin = ORIGINS[originId];
  if (origin) {
    applyEffects(ctx, origin.effects);
    for (const [k, v] of Object.entries(origin.traitBias ?? {})) {
      const key = k as keyof Traits;
      state.traits[key] = clamp100(state.traits[key] + (v ?? 0));
    }
    for (const [k, v] of Object.entries(origin.statBias ?? {})) {
      const key = k as keyof typeof state.stats;
      state.stats[key] = clamp100(state.stats[key] + (v ?? 0));
    }
  }

  const motivation = MOTIVATIONS[motivationId];
  if (motivation) {
    for (const [k, v] of Object.entries(motivation.traitBias ?? {})) {
      const key = k as keyof Traits;
      state.traits[key] = clamp100(state.traits[key] + (v ?? 0));
    }
    for (const defId of motivation.aspirations) {
      applyEffects(ctx, [{ k: 'aspiration', op: 'adopt', defId }]);
    }
  }

  // Habits inherit from personality — a disciplined person starts with better ones.
  state.habits.exercise = clamp100(30 + (state.traits.discipline - 50) * 0.4);
  state.habits.sleep = clamp100(45 + (state.traits.conscientiousness - 50) * 0.3);
  state.habits.saving = clamp100(30 + (state.traits.patience - 50) * 0.45);
  state.habits.learning = clamp100(35 + (state.traits.curiosity - 50) * 0.5);
  state.habits.socialising = clamp100(40 + (state.traits.socialConfidence - 50) * 0.5);
  state.habits.procrastination = clamp100(45 - (state.traits.discipline - 50) * 0.6);
  state.habits.spending = clamp100(40 + (state.traits.impulsiveness - 50) * 0.5);
  state.habits.relationshipCare = clamp100(42 + (state.traits.empathy - 50) * 0.4);
  state.inertia.pressure = clamp100(18 + (state.habits.procrastination - 45) * 0.5);

  state.finance.peakNetWorth = 0;
  state.rng = rng.toState();

  pushLog(state, {
    category: 'life',
    tone: 'milestone',
    title: `${state.identity.name} turns ${startAge}`,
    detail: origin?.label,
    weight: 10,
    tags: ['start'],
  });

  return state;
}
